import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { reviewsApi, safeMethodsApi, usersApi } from '../lib/api';
import Layout from '../components/Layout';
import StatusBadge from '../components/StatusBadge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import {
    CalendarCheck,
    RefreshCw,
    Plus,
    FileText,
    CheckCircle2,
    AlertTriangle,
    AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, capitalize, cn } from '../lib/utils';

const Reviews = () => {
    const { user } = useAuth();
    const [reviews, setReviews] = useState([]);
    const [safeMethods, setSafeMethods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    
    // Form state
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [selectedMethods, setSelectedMethods] = useState([]);
    const [issuesFound, setIssuesFound] = useState('');
    const [actionsTaken, setActionsTaken] = useState('');
    const [trainingCompleted, setTrainingCompleted] = useState('');
    const [compliance, setCompliance] = useState('');
    const [notes, setNotes] = useState('');

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [reviewsRes, methodsRes] = await Promise.all([
                reviewsApi.getAll(),
                safeMethodsApi.getAll()
            ]);
            setReviews(reviewsRes.data);
            setSafeMethods(methodsRes.data);
        } catch (error) {
            console.error('Failed to load data:', error);
            toast.error('Failed to load reviews');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setStartDate('');
        setEndDate('');
        setSelectedMethods([]);
        setIssuesFound('');
        setActionsTaken('');
        setTrainingCompleted('');
        setCompliance('');
        setNotes('');
    };

    const handleMethodToggle = (methodId) => {
        setSelectedMethods(prev => 
            prev.includes(methodId) 
                ? prev.filter(id => id !== methodId)
                : [...prev, methodId]
        );
    };

    const handleSubmit = async () => {
        if (!startDate || !endDate || !compliance) {
            toast.error('Please fill in all required fields');
            return;
        }

        try {
            const response = await reviewsApi.create({
                review_period_start: startDate,
                review_period_end: endDate,
                safe_methods_reviewed: selectedMethods,
                issues_found: issuesFound ? issuesFound.split('\n').filter(Boolean) : [],
                actions_taken: actionsTaken ? actionsTaken.split('\n').filter(Boolean) : [],
                staff_training_completed: trainingCompleted ? trainingCompleted.split('\n').filter(Boolean) : [],
                overall_compliance: compliance,
                notes: notes || null
            });
            
            setReviews([response.data, ...reviews]);
            setDialogOpen(false);
            resetForm();
            toast.success('4-Weekly Review created successfully');
        } catch (error) {
            console.error('Failed to create review:', error);
            toast.error('Failed to create review');
        }
    };

    const getComplianceIcon = (status) => {
        switch (status) {
            case 'safe':
                return <CheckCircle2 className="h-5 w-5 text-emerald-400" />;
            case 'warning':
                return <AlertTriangle className="h-5 w-5 brand-icon" />;
            case 'critical':
                return <AlertCircle className="h-5 w-5 text-rose-400" />;
            default:
                return null;
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <RefreshCw className="h-8 w-8 brand-icon animate-spin" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6" data-testid="reviews-page">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-slate-100">4-Weekly Reviews</h1>
                        <p className="text-slate-400 mt-1">
                            SFBB management review documentation
                        </p>
                    </div>
                    <Dialog open={dialogOpen} onOpenChange={(open) => {
                        setDialogOpen(open);
                        if (!open) resetForm();
                    }}>
                        <DialogTrigger asChild>
                            <Button className="brand-btn" data-testid="create-review-button">
                                <Plus className="h-4 w-4 mr-2" />
                                New Review
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle className="text-slate-100">Create 4-Weekly Review</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 pt-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label className="text-slate-300">Period Start *</Label>
                                        <Input
                                            type="date"
                                            value={startDate}
                                            onChange={(e) => setStartDate(e.target.value)}
                                            className="bg-slate-950 border-slate-800 text-slate-200"
                                            data-testid="review-start-date"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-slate-300">Period End *</Label>
                                        <Input
                                            type="date"
                                            value={endDate}
                                            onChange={(e) => setEndDate(e.target.value)}
                                            className="bg-slate-950 border-slate-800 text-slate-200"
                                            data-testid="review-end-date"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-slate-300">Safe Methods Reviewed</Label>
                                    <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto p-3 bg-slate-950 rounded-lg border border-slate-800">
                                        {safeMethods.map(method => (
                                            <div key={method.id} className="flex items-center gap-2">
                                                <Checkbox
                                                    id={method.id}
                                                    checked={selectedMethods.includes(method.id)}
                                                    onCheckedChange={() => handleMethodToggle(method.id)}
                                                    className="border-slate-600"
                                                />
                                                <label htmlFor={method.id} className="text-sm text-slate-300 cursor-pointer">
                                                    {method.title}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-slate-300">Issues Found (one per line)</Label>
                                    <Textarea
                                        value={issuesFound}
                                        onChange={(e) => setIssuesFound(e.target.value)}
                                        placeholder="Enter any issues found during the review period..."
                                        className="bg-slate-950 border-slate-800 text-slate-200"
                                        data-testid="review-issues-input"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-slate-300">Actions Taken (one per line)</Label>
                                    <Textarea
                                        value={actionsTaken}
                                        onChange={(e) => setActionsTaken(e.target.value)}
                                        placeholder="Enter corrective actions taken..."
                                        className="bg-slate-950 border-slate-800 text-slate-200"
                                        data-testid="review-actions-input"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-slate-300">Staff Training Completed (one per line)</Label>
                                    <Textarea
                                        value={trainingCompleted}
                                        onChange={(e) => setTrainingCompleted(e.target.value)}
                                        placeholder="Enter training completed during this period..."
                                        className="bg-slate-950 border-slate-800 text-slate-200"
                                        data-testid="review-training-input"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-slate-300">Overall Compliance Status *</Label>
                                    <Select value={compliance} onValueChange={setCompliance}>
                                        <SelectTrigger className="bg-slate-950 border-slate-800 text-slate-200" data-testid="review-compliance-select">
                                            <SelectValue placeholder="Select compliance status" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-slate-900 border-slate-800">
                                            <SelectItem value="safe" className="text-slate-200 focus:bg-slate-800">
                                                <span className="flex items-center gap-2">
                                                    <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                                                    Safe - All procedures followed
                                                </span>
                                            </SelectItem>
                                            <SelectItem value="warning" className="text-slate-200 focus:bg-slate-800">
                                                <span className="flex items-center gap-2">
                                                    <AlertTriangle className="h-4 w-4 brand-icon" />
                                                    Warning - Minor issues identified
                                                </span>
                                            </SelectItem>
                                            <SelectItem value="critical" className="text-slate-200 focus:bg-slate-800">
                                                <span className="flex items-center gap-2">
                                                    <AlertCircle className="h-4 w-4 text-rose-400" />
                                                    Critical - Urgent action required
                                                </span>
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-slate-300">Additional Notes</Label>
                                    <Textarea
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                        placeholder="Any additional notes..."
                                        className="bg-slate-950 border-slate-800 text-slate-200"
                                        data-testid="review-notes-input"
                                    />
                                </div>

                                <Button
                                    onClick={handleSubmit}
                                    className="w-full brand-btn"
                                    data-testid="submit-review-button"
                                >
                                    Create Review
                                </Button>
                            </div>
                        </DialogContent>
                    </Dialog>
                </div>

                {/* Reviews List */}
                {reviews.length > 0 ? (
                    <div className="space-y-4">
                        {reviews.map(review => (
                            <Card key={review.id} className="bg-slate-900 border-slate-800" data-testid={`review-card-${review.id}`}>
                                <CardHeader className="border-b border-slate-800">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-4">
                                            <div className={cn(
                                                'h-12 w-12 rounded-xl flex items-center justify-center',
                                                review.overall_compliance === 'safe' ? 'bg-emerald-500/10' :
                                                review.overall_compliance === 'warning' ? 'bg-amber-500/10' : 'bg-rose-500/10'
                                            )}>
                                                {getComplianceIcon(review.overall_compliance)}
                                            </div>
                                            <div>
                                                <CardTitle className="text-lg text-slate-100">
                                                    {formatDate(review.review_period_start)} - {formatDate(review.review_period_end)}
                                                </CardTitle>
                                                <p className="text-sm text-slate-400">
                                                    Reviewed by {review.reviewed_by_name} on {formatDate(review.created_at)}
                                                </p>
                                            </div>
                                        </div>
                                        <StatusBadge status={review.overall_compliance} />
                                    </div>
                                </CardHeader>
                                <CardContent className="pt-4 space-y-4">
                                    <div className="grid md:grid-cols-2 gap-4">
                                        {review.safe_methods_reviewed.length > 0 && (
                                            <div>
                                                <h4 className="text-sm font-medium text-slate-400 mb-2">Safe Methods Reviewed</h4>
                                                <p className="text-sm text-slate-300">
                                                    {review.safe_methods_reviewed.length} method(s) reviewed
                                                </p>
                                            </div>
                                        )}
                                        
                                        {review.issues_found.length > 0 && (
                                            <div>
                                                <h4 className="text-sm font-medium text-slate-400 mb-2">Issues Found</h4>
                                                <ul className="text-sm text-slate-300 space-y-1">
                                                    {review.issues_found.map((issue, idx) => (
                                                        <li key={idx} className="flex items-start gap-2">
                                                            <span className="text-rose-400">•</span>
                                                            {issue}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                        
                                        {review.actions_taken.length > 0 && (
                                            <div>
                                                <h4 className="text-sm font-medium text-slate-400 mb-2">Actions Taken</h4>
                                                <ul className="text-sm text-slate-300 space-y-1">
                                                    {review.actions_taken.map((action, idx) => (
                                                        <li key={idx} className="flex items-start gap-2">
                                                            <span className="text-emerald-400">•</span>
                                                            {action}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                        
                                        {review.staff_training_completed.length > 0 && (
                                            <div>
                                                <h4 className="text-sm font-medium text-slate-400 mb-2">Training Completed</h4>
                                                <ul className="text-sm text-slate-300 space-y-1">
                                                    {review.staff_training_completed.map((training, idx) => (
                                                        <li key={idx} className="flex items-start gap-2">
                                                            <span className="text-amber-400">•</span>
                                                            {training}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>

                                    {review.notes && (
                                        <div className="pt-4 border-t border-slate-800">
                                            <h4 className="text-sm font-medium text-slate-400 mb-2">Notes</h4>
                                            <p className="text-sm text-slate-300">{review.notes}</p>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                ) : (
                    <Card className="bg-slate-900 border-slate-800">
                        <CardContent className="py-12">
                            <div className="empty-state">
                                <CalendarCheck className="empty-state-icon" />
                                <p className="empty-state-title">No reviews</p>
                                <p className="empty-state-description">
                                    Create your first 4-weekly SFBB review to document compliance.
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>
        </Layout>
    );
};

export default Reviews;
